import React, { useCallback, useEffect, useRef, useState } from 'react';
import { CircularProgress, IconButton } from '@mui/material';
import { PlayArrow as PlayIcon, Stop as StopIcon } from '@mui/icons-material';
import { Storage } from '@aws-amplify/storage';
import useStyles from './styles';
import { useSnackbar } from 'notistack';

export default function AudioMiniPreview({ audioId, blob, ...other }) {
    const classes = useStyles(other);
    const { enqueueSnackbar } = useSnackbar();

    const [isPlaying, setPlaying] = useState(false);
    const [isBusy, setBusy] = useState(false);
    const [isReady, setReady] = useState(false);
    const [progress, setProgress] = useState(0);

    /**
     * Ref for an optional cleanup function to be run to clean up generated
     *  src URLs.
     * @type {React.MutableRefObject<() => void>}
     */
    const _srcCleanupRef = useRef();

    const cleanUpSrc = useCallback(() => {
        if (_srcCleanupRef.current) {
            _srcCleanupRef.current();
            _srcCleanupRef.current = null;
        }
    }, []);

    /** @type {React.MutableRefObject<HTMLAudioElement>} */
    const audioElRef = useRef();

    const getSrcAsync = useCallback(async () => {
        cleanUpSrc();

        if (audioId) {
            try {
                return await Storage.get(`sounds/${audioId}`, {
                    level: 'public',
                    customPrefix: { public: '' },
                });
            }
            catch (error) {
                enqueueSnackbar('Failed to get audio URL from AWS', { variant: 'error' });
                throw error;
            }
        }
        else if (blob) {
            const url = URL.createObjectURL(blob);
            _srcCleanupRef.current = () => URL.revokeObjectURL(blob);
            return url;
        }
        else {
            throw new Error('No audioId or blob supplied to AudioMiniPreview');
        }
    }, [audioId, blob]);

    const createAudioElement = useCallback(async () => {
        const src = await getSrcAsync();

        const el = document.createElement('audio');
        el.src = src;

        el.addEventListener('timeupdate', () => setProgress(el.currentTime / el.duration));

        el.addEventListener('ended', () => {
            setPlaying(false);
            setProgress(0);
        });

        return el;
    }, [getSrcAsync]);

    const play = useCallback(async event => {
        event.stopPropagation();

        try {
            setBusy(true);

            if (!audioElRef.current) {
                audioElRef.current = await createAudioElement();
            }
        }
        finally { setBusy(false); }

        setPlaying(true);

        try {
            await audioElRef.current.play();
        }
        catch (error) {
            enqueueSnackbar('Error playing audio', { variant: 'error' });
            setPlaying(false);

            throw error;
        }

        setReady(true);
    }, [createAudioElement]);

    const stop = useCallback(event => {
        event.stopPropagation();

        if (audioElRef.current) {
            audioElRef.current.pause();
            audioElRef.current.currentTime = 0;
        }
        setPlaying(false);
    }, []);

    // On umount, or audio change, remove the old audio element.
    useEffect(() => {
        return () => {
            if (audioElRef.current) {
                audioElRef.current.pause();
                audioElRef.current.remove();
                audioElRef.current = null;

                setPlaying(false);
                setProgress(0);
                setReady(false);
                setBusy(false);
            }

            cleanUpSrc();
        };
    }, [audioId]);

    return (
        <IconButton
            className={classes.root}
            disabled={isBusy || (!audioId && !blob)}
            onClick={isPlaying ? stop : play}
            {...other}
            size="large">
            {isPlaying ? <StopIcon /> : <PlayIcon />}

            {isPlaying && (
                <CircularProgress
                    className={classes.progress}
                    variant={isReady ? 'determinate' : 'indeterminate'}
                    value={progress * 100}
                />
            )}
        </IconButton>
    );
}